import {
  Circuit,
  EnergySource,
  FormCircuit,
  FormGlazedSurface,
  FormProcessInputType,
  FormProcessOutputType,
  FormProcessType,
  FormValType,
  GlazedSurface,
  IsolationType,
  KeyNumType,
  ProcessFunctionType,
  RevertFunctionType
} from '@/helper/Types'
import projectStore from '@/store/ProjectStore'
import { formatLanguage, parseFloatLocale } from '@/helper/Translations'
import { generateRandomId, refValue } from '@/helper/tools'
import FormController from '@/controllers/FormController'
import { BuildingHull } from '@/models/BuildingHull.ts'

/**
 * Process functions: backend -> frontend
 * Revert functions: frontend -> backend
 */
export class ProcessFunctions {
  private static processFunctions: Partial<Record<FormProcessType, ProcessFunctionType>> = {
    [FormProcessType.BASIC]: ProcessFunctions.basicProcessFunction,
    [FormProcessType.INT]: ProcessFunctions.intProcessFunction,
    [FormProcessType.BOOLEAN]: ProcessFunctions.booleanProcessFunction,
    [FormProcessType.KEY_NUM]: ProcessFunctions.keyNumProcessFunction,
    [FormProcessType.INTL_NUMBER]: ProcessFunctions.intlNumberProcessFunction,
    [FormProcessType.GLAZED_SURFACE]: ProcessFunctions.glazedSurfacesProcessFunction,
    [FormProcessType.ISOLATION]: ProcessFunctions.isolationProcessFunction,
    [FormProcessType.CIRCUIT]: ProcessFunctions.circuitProcessFunction,
    [FormProcessType.ACCESSORY]: ProcessFunctions.accessoriesProcessFunction,
    [FormProcessType.ADDITIONAL_PRODUCT_ID]: ProcessFunctions.additionalProductIdProcessFunction,
    [FormProcessType.ARRAY]: ProcessFunctions.stringArrayProcessFunction,
    [FormProcessType.OBJECT]: ProcessFunctions.objectProcessFunction,
    [FormProcessType.BUILDING_HULL]: ProcessFunctions.buildingHullProcessFunction,
  }

  private static revertFunctions: Partial<Record<FormProcessType, RevertFunctionType>> = {
    [FormProcessType.BASIC]: ProcessFunctions.basicRevertFunction,
    [FormProcessType.BOOLEAN]: ProcessFunctions.booleanRevertFunction,
    [FormProcessType.INT]: ProcessFunctions.intRevertFunction,
    [FormProcessType.DECIMAL]: ProcessFunctions.decimalRevertFunction,
    [FormProcessType.KEY_NUM]: ProcessFunctions.keyNumRevertFunction,
    [FormProcessType.INTL_NUMBER]: ProcessFunctions.intlNumberRevertFunction,
    [FormProcessType.GLAZED_SURFACE]: ProcessFunctions.glazedSurfaceRevertFunction,
    [FormProcessType.ISOLATION]: ProcessFunctions.isolationRevertFunction,
    [FormProcessType.CIRCUIT]: ProcessFunctions.circuitRevertFunction,
    [FormProcessType.ACCESSORY]: ProcessFunctions.accessoriesRevertFunction,
    [FormProcessType.ADDITIONAL_PRODUCT_ID]: ProcessFunctions.additionalProductIdRevertFunction,
    [FormProcessType.ENERGY_SOURCE]: ProcessFunctions.energySourceRevertFunction,
    [FormProcessType.ARRAY]: ProcessFunctions.stringArrayRevertFunction,
    [FormProcessType.OBJECT]: ProcessFunctions.objectRevertFunction,
    [FormProcessType.BUILDING_HULL]: ProcessFunctions.buildingHullRevertFunction,
  }

  static getProcessFunction(type: FormProcessType): ProcessFunctionType {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return ProcessFunctions.processFunctions[type] || ((val: FormProcessInputType) => '')
  }

  static getRevertFunction(type: FormProcessType): RevertFunctionType {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return ProcessFunctions.revertFunctions[type] || ((val: FormProcessOutputType) => '')
  }

  static additionalProductIdProcessFunction(val: FormProcessInputType): string {
    return ProcessFunctions.basicProcessFunction(val)
      ? ProcessFunctions.basicProcessFunction(val) +
      '.' +
      ProcessFunctions.basicProcessFunction(projectStore.projectProperties.heatPumpId.val)
      : ''
  }

  static additionalProductIdRevertFunction(val: FormProcessOutputType | FormValType): FormProcessInputType {
    return val ? val.toString().split('.')[0] : undefined
  }

  static basicProcessFunction(val: FormProcessInputType): string {
    return val == undefined ? '' : val.toString()
  }

  static stringArrayProcessFunction(val: FormProcessInputType): FormProcessOutputType {
    return val as string[]
  }

  static stringArrayRevertFunction(val: FormProcessOutputType | FormValType): FormProcessInputType {
    return val ? val : undefined
  }

  static intProcessFunction(val: FormProcessInputType | FormValType): string {
    return val ? (val as string) : ''
  }

  static basicRevertFunction(val: FormProcessOutputType | FormValType): FormProcessInputType {
    return val ? val : undefined
  }

  static booleanProcessFunction(val: FormProcessInputType): FormProcessOutputType {
    return !!val
  }

  static booleanRevertFunction(val: FormProcessOutputType | FormValType): boolean {
    return !!val
  }

  static keyNumProcessFunction(val: FormProcessInputType): KeyNumType {
    return val == undefined
      ? ({} as KeyNumType)
      : (JSON.parse(
        JSON.stringify(val, (key: string, value: any) => {
          if (typeof value === 'number') {
            return value.toString()
          }
          return value
        })
      ) as KeyNumType)
  }

  static keyNumRevertFunction(val: FormProcessOutputType): FormProcessInputType {
    return Object.keys(val).length ? JSON.parse(JSON.stringify(val)) : undefined
  }

  static objectProcessFunction(val: FormProcessInputType): FormProcessOutputType {
    if (val == undefined) {
      return {}
    }
    return JSON.parse(JSON.stringify(val))
  }

  static objectRevertFunction(val: FormProcessOutputType): FormProcessInputType {
    return Object.keys(val).length ? JSON.parse(JSON.stringify(val)) : undefined
  }

  static accessoriesProcessFunction(val: FormProcessInputType): KeyNumType {
    return val === undefined ? {} : (Object.assign({}, val) as KeyNumType)
  }

  static accessoriesRevertFunction(val: FormProcessOutputType | FormValType): KeyNumType | undefined {
    const revertedObject: KeyNumType = {}
    if (val === '' || val === false) {
      return revertedObject
    }

    for (const item of Object.keys(val)) {
      if ((val as KeyNumType)[item] > 0) {
        revertedObject[item] = (val as KeyNumType)[item]
      }
    }
    return Object.keys(revertedObject).length ? revertedObject : undefined
  }

  static intlNumberProcessFunction(val: FormProcessInputType): string {
    return val ? val.toLocaleString(formatLanguage(), { maximumFractionDigits: 6 }) : ''
  }

  static intlNumberRevertFunction(val: FormProcessOutputType | FormValType): number | undefined {
    return val ? parseFloatLocale(val as string) : undefined
  }

  static intRevertFunction(val: FormProcessOutputType | FormValType): number | undefined {
    return val ? parseInt(val as string) : undefined
  }

  static decimalRevertFunction(val: FormProcessOutputType | FormValType): number | undefined {
    return val ? parseFloat(val.toString().replace(',', '.')) : undefined
  }

  static energySourceRevertFunction(val: FormProcessOutputType | FormValType): EnergySource | undefined {
    return val ? (val as EnergySource) : undefined
  }

  static isolationProcessFunction(val: FormProcessInputType): IsolationType[] {
    return (val || []) as IsolationType[]
  }

  static isolationRevertFunction(val: FormProcessOutputType | FormValType): IsolationType[] | undefined {
    return Array.isArray(val) && val.length ? (val as IsolationType[]) : undefined
  }

  static glazedSurfacesProcessFunction(val: FormProcessInputType): FormGlazedSurface[] {
    return Array.isArray(val) && val.length
      ? (val as GlazedSurface[]).map((item: GlazedSurface) => {
        return {
          id: generateRandomId(),
          name: item.name || '',
          orientation: item.orientation,
          width: (item.width || '').toLocaleString(formatLanguage(), {
            maximumFractionDigits: 6
          }),
          height: (item.height || '').toLocaleString(formatLanguage(), {
            maximumFractionDigits: 6
          }),
          shaded: item.shaded
        } as FormGlazedSurface
      })
      : [FormController.getEmptyGlacedSurface()]
  }

  static glazedSurfaceRevertFunction(val: FormProcessOutputType | FormValType): GlazedSurface[] | undefined {
    const res =
      Array.isArray(val) && val.length
        ? (val as FormGlazedSurface[])
          .filter((item: FormGlazedSurface) => !!refValue(item.name).trim())
          .map((item: FormGlazedSurface) => {
            return {
              name: item.name,
              orientation: item.orientation,
              width: parseFloatLocale(item.width),
              height: parseFloatLocale(item.height),
              shaded: item.shaded
            } as GlazedSurface
          })
        : undefined
    return res?.length ? res : undefined
  }

  static circuitProcessFunction(val: FormProcessInputType): FormCircuit[] {
    return Array.isArray(val) && val.length
      ? (val as Circuit[]).map((item: Circuit) => {
        return {
          id: generateRandomId(),
          flowTemperature: item.flowTemperature?.toString(),
          systemType: item.systemType,
          cooling: item.cooling
        } as FormCircuit
      })
      : [FormController.getEmptyCircuit()]
  }

  static circuitRevertFunction(val: FormProcessOutputType | FormValType): Circuit[] | undefined {
    const res = Array.isArray(val)
      ? (val as FormCircuit[])
        .filter((v: FormCircuit) => v.systemType != undefined || v.flowTemperature != undefined)
        .map((item: FormCircuit) => {
          return {
            systemType: item.systemType,
            flowTemperature: item.flowTemperature ? parseInt(refValue(item.flowTemperature)) : undefined,
            cooling: item.cooling
          } as Circuit
        })
      : undefined
    return res?.length ? res : undefined
  }

  static buildingHullProcessFunction(buildingHull: BuildingHull | undefined): BuildingHull | undefined {
    if (buildingHull?.stories?.length) {
      const hull = new BuildingHull(buildingHull.stories)
      hull.stories.forEach((s) => {
        //@ts-ignore
        s.ceilingHeight = s.ceilingHeight?.toString() || ''
        //@ts-ignore
        s.length = s.length?.toString() || ''
        //@ts-ignore
        s.width = s.width?.toString() || ''
        //@ts-ignore
        s.livingSpace = s.livingSpace?.toString() || ''
      })
      return hull
    } else {
      return undefined
    }
  }

  static buildingHullRevertFunction(buildingHull: BuildingHull): BuildingHull | undefined {
    if (!buildingHull?.stories || buildingHull.stories.length === 0) {
      return undefined
    }

    return new BuildingHull(buildingHull.stories.map(s => ({
      ceilingHeight: s.ceilingHeight ? parseFloatLocale(s.ceilingHeight?.toString() || '') : undefined,
      bottomStory: s.bottomStory,
      topStory: s.topStory,
      length: s.length ? parseFloatLocale(s.length?.toString() || '') : undefined,
      livingSpace: s.livingSpace ? parseFloatLocale(s.livingSpace?.toString() || '') : undefined,
      width: s.width ? parseFloatLocale(s.width?.toString() || '') : undefined,
    })))
  }
}
